import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { isAxiosError } from 'axios';
import { axiosInstance } from 'common/utils/axios';
import { throttleRequests } from './api-actions';
/**
 *
 * Broken out for easier testing and better portability
 *
 * @param {string} endpoint
 * @param {actionCreator} loadingAction
 * @param {actionCreator} successAction
 * @param {actionCreator} errorAcction
 */
export const fetchAPIHelper = (
  endpoint,
  loadingAction,
  successAction,
  errorAction,
  retryAction,
  headers = {},
) => dispatch => {
  dispatch(loadingAction(true));
  axiosInstance
    .get(endpoint, { headers })
    .then(response => {
      const { data } = response;
      dispatch(successAction(data));
    })
    .catch(err => {
      if (isAxiosError(err)) {
        if (err.status === 429) {
          dispatch(throttleRequests(true));
        } else {
          // Error tracking with new browser-rum API using selective imports
          const errorCtx = rumErrorCtx()
            .component('fetchAPIHelper')
            .message(`HTTP request failed`)
            .severity(SEVERITY.MEDIUM)
            .custom('endpoint', endpoint)
            .custom('status', err.status)
            .custom('errorMessage', err.message)
            .build();
          
          ddRum.addError(err, errorCtx);
          
          if (retryAction) dispatch(retryAction());
          dispatch(errorAction(true));
        }
      }
    });
};

export const fetchWithOption = (
  endpoint,
  option = {},
  action = {},
) => dispatch => {
  const { start, success, error, retry } = action;
  if (start) {
    dispatch(start(true));
  }

  return fetch(endpoint, {
    ...option,
    body: JSON.stringify(option.body),
  })
    .then(response => {
      if (!response.ok) {
        // Error tracking with new browser-rum API using selective imports
        const errorCtx = rumErrorCtx()
          .component('fetchWithOption')
          .message(`HTTP Non-success response`)
          .severity(SEVERITY.MEDIUM)
          .custom('endpoint', endpoint)
          .custom('status', response.status)
          .custom('statusText', response.statusText)
          .build();
        
        ddRum.addError(new Error(response.statusText), errorCtx);
        
        if (response.status === 429) {
          /**
           * Case: We're being throttled
           * Reject response here and handle dispatch in catch()
           */
          return Promise.reject(response);
        }
        throw Error(response.statusText);
      }
      return response;
    })
    .then(response => (response ? response.json() : response))
    .then(data => {
      if (success) {
        dispatch(success(data));
      }
    })
    .catch(err => {
      if (err.status === 429) {
        dispatch(throttleRequests(true));
      } else {
        // Error tracking with new browser-rum API using selective imports
        const errorCtx = rumErrorCtx()
          .component('fetchWithOption')
          .message(`HTTP request failed`)
          .severity(SEVERITY.MEDIUM)
          .custom('endpoint', endpoint)
          .custom('status', err.status)
          .custom('errorMessage', err.message || String(err))
          .build();
        
        ddRum.addError(err instanceof Error ? err : new Error(String(err)), errorCtx);
        
        if (retry) dispatch(retry());
        if (error) {
          dispatch(error(true));
        }
      }
    });
};

export default fetchAPIHelper;
